import { useState, useCallback, useMemo, useEffect } from "react";
import { CreateWorkspaceLoader } from "./CreateWorkspaceLoader";
import { CreateWorkspaceLoaderContext } from "./CreateWorkspaceLoaderContext";

export const CreateWorkspaceLoaderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  const [canFinishLoading, setCanFinishLoading] = useState(false);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave this page? You may lose your progress.";
        return e.returnValue;
      }
      return null;
    };
    if (isLoading) {
      window.addEventListener("beforeunload", onBeforeUnload);
    }
    return () => {
      if (isLoading) {
        window.removeEventListener("beforeunload", onBeforeUnload);
      }
    };
  }, [isLoading]);

  const cancelLoading = useCallback(() => {
    setIsLoading(false);
    setCanFinishLoading(false);
  }, []);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    return {
      cancel: () => cancelLoading(),
      setCanFinish: () => setCanFinishLoading(true),
    };
  }, [cancelLoading]);

  const contextValue = useMemo(
    () => ({
      startLoading,
      isLoading,
      cancel: cancelLoading,
      canFinishLoading,
    }),
    [startLoading, isLoading, cancelLoading, canFinishLoading]
  );

  return (
    <CreateWorkspaceLoaderContext.Provider value={contextValue}>
      <CreateWorkspaceLoader />
      {children}
    </CreateWorkspaceLoaderContext.Provider>
  );
};
